import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, iif, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { Consent, ConsentGroup, ConsentStatus, ConsentTextId } from '@fcom/common/interfaces';
import { loginToken, profileOrUndefinedIfNotLoggedIn, profileType } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx';
import { ConsentService } from '@fcom/common/login';
import {
  NewsletterSignupPayload,
  NewsletterSignupResponse,
  NewsLetterSource,
} from '@fcom/cms/interfaces/newsletter-signup.interface';
import { LanguageService } from '@fcom/ui-translate';
import { NewsletterSignupService } from '@fcom/cms';
import { SentryLogger } from '@fcom/core/services';
import { CheckboxComponent } from '@fcom/ui-components';
import { TypedFormArray, TypedFormGroup } from '@fcom/service-forms';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { unsubscribe } from '@fcom/core/utils';

export enum MarketingConsentType {
  NEWSLETTER = 'NEWSLETTER',
  FLIGHT_OFFERS = 'FLIGHT_OFFERS',
  MEMBER_OFFERS = 'MEMBER_OFFERS',
}

interface NewsLetterForm {
  advertising: FormControl<boolean>;
  marketingNewsletter: FormControl<boolean>;
}

@Component({
  selector: 'fin-pax-details-marketing-consent',
  templateUrl: './pax-details-marketing-consent.component.html',
})
export class PaxDetailsMarketingConsentComponent implements OnInit, OnDestroy {
  public readonly MarketingConsentType = MarketingConsentType;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() passengers: TypedFormArray<TypedFormGroup<UntypedFormGroup | UntypedFormControl>>;

  @ViewChild('consentCheck') private consentCheck: CheckboxComponent;

  marketingConsentOpen = true;
  marketingConsentType$: Observable<MarketingConsentType | undefined> = of(undefined);
  consents$: Observable<Consent[]>;
  formGroup: FormGroup<NewsLetterForm>;
  subscriptions = new Subscription();

  constructor(
    private router: Router,
    private consentService: ConsentService,
    private languageService: LanguageService,
    private newsletterService: NewsletterSignupService,
    private sentryLogger: SentryLogger,
    private store$: Store<BookingAppState>
  ) {}

  ngOnInit(): void {
    this.marketingConsentType$ = this.getMarketingConsentType();
    this.formGroup = new FormGroup({
      marketingNewsletter: new FormControl<boolean>(false),
      advertising: new FormControl<boolean>({ value: false, disabled: true }),
    });

    this.subscriptions.add(
      this.formGroup.controls.marketingNewsletter.valueChanges.subscribe((marketingEnabled) => {
        if (marketingEnabled) {
          this.formGroup.controls.advertising.enable();
        } else {
          this.formGroup.controls.advertising.setValue(false);
          this.formGroup.controls.advertising.disable();
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  private getMarketingConsentType(): Observable<MarketingConsentType | undefined> {
    return this.store$.pipe(
      profileOrUndefinedIfNotLoggedIn(),
      switchMap((userProfile) =>
        iif(() => !!userProfile, this.getMemberConsent(), of(MarketingConsentType.NEWSLETTER))
      ),
      finShare()
    );
  }

  private getMemberConsent(): Observable<MarketingConsentType | undefined> {
    this.consents$ = combineLatest([
      this.store$.pipe(loginToken(), filter(Boolean), take(1)),
      this.store$.pipe(profileType(), filter(Boolean), take(1)),
    ]).pipe(
      switchMap(([token, type]) => this.consentService.getMarketingConsents({ token }, type)),
      map((consentGroup: ConsentGroup) => consentGroup?.consents)
    );

    return this.consents$.pipe(
      map((consents) => {
        const flightOffersAccepted = consents?.find(
          (c) => c.consentTextId === ConsentTextId.FLIGHT_OFFERS && c.consentStatus === ConsentStatus.ACCEPTED
        );
        if (!flightOffersAccepted) {
          return MarketingConsentType.FLIGHT_OFFERS;
        }
        const memberOffersAccepted = consents?.find(
          (c) => c.consentTextId === ConsentTextId.MEMBER_OFFERS && c.consentStatus === ConsentStatus.ACCEPTED
        );
        if (!memberOffersAccepted) {
          return MarketingConsentType.MEMBER_OFFERS;
        }
        return undefined;
      })
    );
  }

  public setMarketingConsent(): Observable<unknown> {
    return this.marketingConsentType$.pipe(
      take(1),
      switchMap((type) => {
        switch (type) {
          case MarketingConsentType.FLIGHT_OFFERS:
            return this.addConsent(ConsentTextId.FLIGHT_OFFERS);
          case MarketingConsentType.MEMBER_OFFERS:
            return this.addConsent(ConsentTextId.MEMBER_OFFERS);
          case MarketingConsentType.NEWSLETTER:
          default:
            return this.subscribeToNewsletter();
        }
      }),
      catchError((e: unknown) => {
        this.sentryLogger.error('Failed to set marketing consents', e);
        return of(false);
      })
    );
  }

  private addConsent(textId: string): Observable<unknown> {
    if (!this.consentCheck?.checked) {
      return of(false);
    }
    return this.consents$.pipe(
      map((consents) =>
        consents?.map((c: Consent) => ({
          ...c,
          consentStatus: c.consentTextId === textId ? ConsentStatus.ACCEPTED : c.consentStatus,
        }))
      ),
      withLatestFrom(this.store$.pipe(loginToken())),
      switchMap(([consents, token]) => this.consentService.sendConsents({ token }, consents, 'booking_flow'))
    );
  }

  private subscribeToNewsletter(): Observable<NewsletterSignupResponse> {
    // Skip newsletter subscribing if none of the checkboxes have been checked
    if (
      !this.consentCheck?.checked &&
      !this.formGroup.get('marketingNewsletter').value &&
      !this.formGroup.get('advertising').value
    ) {
      return of(null);
    }
    const firstPassenger = this.passengers.controls[0];
    return combineLatest([this.languageService.countryCode, this.languageService.langKey]).pipe(
      switchMap(([country, language]: [string, string]) => {
        const payload: NewsletterSignupPayload = {
          firstName: firstPassenger.get('firstName').value,
          lastName: firstPassenger.get('lastName').value,
          email: firstPassenger.get('email').value,
          country: country?.toUpperCase(),
          language: language?.toUpperCase(),
          pageUrl: this.router.url,
          source: NewsLetterSource.BOOKING_FLOW,
          marketingNewsletter: this.formGroup.get('marketingNewsletter').value,
          advertising: this.formGroup.get('advertising').value,
        };
        return this.newsletterService.postSubscribeRequest(payload);
      })
    );
  }
}
